import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Edit as BaseEdit,
  SimpleForm,
  TextInput,
  NumberInput,
  required,
  ArrayInput,
  SimpleFormIterator,
  email,
  DateInput,
  SelectInput,
  Toolbar,
  SaveButton,
  DeleteButton,
  useNotify,
  useRedirect,
  useRefresh,
  BooleanInput,
  FormDataConsumer,
  useGetOne,
} from "react-admin";
import Map from "../../components/Map";
import countryCodes from "./phone_code.json";
import moment from "moment";
import { useNotification } from "../../NotificationContext";

const PREFIX = "Edit";

const classes = {
  inlineBlock: `${PREFIX}-inlineBlock`,
};

const StyledBaseEdit = styled(BaseEdit)({
  [`& .${classes.inlineBlock}`]: {
    display: "inline-flex",
    marginRight: "1rem",
  },
});

// CustomToolbar con los botones de Guardar y Eliminar
const CustomToolbar = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const handleDeleteSuccess = () => {
    notify("Eliminado con éxito", { type: "success" });
    redirect("enterprise/customers"); // Redirecciona a la lista de clientes o la ruta correspondiente
    refresh();
  };

  const handleDeleteFailure = (error) => {
    notify(`Error: ${error.message}`, { type: "warning" });
  };

  return (
    <Toolbar {...props} style={{ display: "flex", justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton
        mutationMode="pessimistic"
        onSuccess={handleDeleteSuccess}
        onFailure={handleDeleteFailure}
        confirmTitle="Confirmación de eliminación"
        confirmContent="¿Estás seguro de que deseas eliminar este registro? Esta acción no se puede deshacer."
      />
    </Toolbar>
  );
};

export const Edit = ({ ...props }) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState("591");
  const { addNotification } = useNotification();
  const notify = useNotify();

  const handleCountryChange = (event) => {
    setSelectedCountryCode(event.target.value);
  };

  const handleError = (error) => {
    if (error.message === "Este número de teléfono ya está registrado") {
      notify("Este número de teléfono ya está registrado", { type: "error" });
    } else {
      notify("Error al actualizar el cliente", { type: "error" });
    }
  };

  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;
  const { data: provider } = useGetOne("providers", { id: providerId });
  return (
    <StyledBaseEdit
      {...props}
      onError={handleError}
      transform={(data) => {
        const today = moment().format("YYYY-MM-DD"); // definir primero

        if (!data.birthdate) return data;

        const stored = JSON.parse(localStorage.getItem("birthdaysShown") || "{}");

        if (stored.date !== today) {
          stored.date = today;
          stored.ids = [];
        }

        const updatedIds = [...stored.ids];

        const birthdate = moment.utc(data.birthdate);
        const todayMoment = moment();
        const tomorrow = moment().add(1, "day");

        const birthdayThisYear = moment().month(birthdate.month()).date(birthdate.date());

        const isToday = birthdayThisYear.isSame(todayMoment, "day");
        const isTomorrow = birthdayThisYear.isSame(tomorrow, "day");

        if (isToday || isTomorrow) {
          const id = `birthday-${data.fullName}-${isToday ? "today" : "tomorrow"}`;

          if (!updatedIds.includes(id)) {
            console.log("se agregó notificación");
            addNotification({
              id,
              title: "🎉 Cumpleaños",
              message: `${isToday ? "Hoy" : "Mañana"} cumple años ${data.fullName}`,
            });
            updatedIds.push(id);

            localStorage.setItem("birthdaysShown", JSON.stringify({ date: today, ids: updatedIds }));
          }
        }
        console.log("Datos antes de enviar:", data);
        return data;
      }}
    >
      <SimpleForm toolbar={<CustomToolbar />}>
        <TextInput validate={[required()]} source="fullName" label="Nombre Completo" />
        <SelectInput
          label="Código de País"
          source="countryCode"
          choices={countryCodes.paises}
          optionText={(value) => `${value.name} ( +${value.dial_code} )`}
          optionValue="dial_code"
          onChange={handleCountryChange}
          validate={[required()]}
          defaultValue={countryCodes.paises.find((e) => e.code === "BO")?.dial_code}
        />
        <NumberInput source="phone" label="Teléfono/Celular" required={required()} />
        <TextInput validate={[email()]} source="email" label="Email" />
        <DateInput
          label="Fecha de Nacimiento"
          source="birthdate"
          parse={(value) => {
            if (!value) return null;
            return moment(value).format("YYYY-MM-DD");
          }}
          format={(value) => {
            if (!value) return null;
            return moment.utc(value).format("YYYY-MM-DD");
          }}
        />
        <SelectInput
          label="Género"
          source="gender"
          choices={[
            { id: "hombre", name: "Hombre" },
            { id: "mujer", name: "Mujer" },
          ]}
        />
        {provider?.reminderConfirmed && (
          <>
            <BooleanInput source="reminderEnabled" label="Activar Recordatorio" />
            <FormDataConsumer>
              {({ formData }) =>
                formData?.reminderEnabled ? (
                  <span style={{ marginBottom: 20, color: "gray" }}>
                    {" "}
                    * La notificación del recordatorio <br /> enviará a las 08:00 am
                  </span>
                ) : null
              }
            </FormDataConsumer>
          </>
        )}

        <ArrayInput source="addresses" label="Direcciones">
          <SimpleFormIterator>
            <Map />
            <TextInput validate={[required()]} source="name" label="Nombre" />
            <TextInput validate={[required()]} source="street" label="Calle" />
            <TextInput source="house" label="Número de casa" />
            <TextInput source="reference" label="Referencia" />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </StyledBaseEdit>
  );
};
